/**
 * Der Symbolsatz.
 *
 * Lucide-Stil, wie im Design-System festgelegt: 24×24-Zeichenfläche, ~2 px
 * Strich, runde Enden, `currentColor`. Die Symbole sind **selbst gezeichnet**
 * und keine Abhängigkeit — der Satz im Design-System-Projekt
 * (`ui_kits/dashboard/icons.jsx`) ist ausdrücklich ein Stellvertreter, und für
 * gut zwanzig Formen eine Bibliothek samt Lizenzprüfung aufzunehmen, wäre
 * unverhältnismäßig. Wird der Bedarf größer, ist [Lucide](https://lucide.dev)
 * vorgesehen; die Schnittstelle hier ist bewusst dieselbe (`size`).
 *
 * **Alle Symbole sind dekorativ.** Sie tragen `aria-hidden`, weil die Bedeutung
 * immer am Text daneben hängt — „Farbe ist nie der alleinige Träger einer
 * Aussage" gilt für Symbole genauso. Steht ein Symbol wirklich allein, gehört
 * die Beschriftung an das umgebende Element (siehe `IconButton`).
 */
import type { ReactNode, SVGProps } from "react";

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "children"> {
  /** Kantenlänge in Pixeln. Der Strich skaliert mit. */
  readonly size?: number;
}

export type Icon = (props: IconProps) => ReactNode;

/**
 * Baut aus einer Zeichnung ein Symbol.
 *
 * Der Strich wird über `strokeWidth` bei kleinen Größen leicht angehoben: bei
 * 16 px wirkt ein 2-px-Strich auf 24er-Fläche ausgedünnt, weil er auf 1,33 px
 * herunterskaliert und dann kein volles Gerätepixel mehr trifft.
 */
function icon(name: string, draw: ReactNode): Icon {
  function Glyph({ size = 20, ...rest }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={size <= 16 ? 2.25 : 2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
        {...rest}
      >
        {draw}
      </svg>
    );
  }

  Glyph.displayName = name;
  return Glyph;
}

/** Übersicht. */
export const IconHome = icon(
  "IconHome",
  <>
    <path d="M3 10.6 12 3l9 7.6" />
    <path d="M5.6 9.4V21h12.8V9.4" />
    <path d="M9.6 21v-6h4.8v6" />
  </>,
);

/** Strom — die Sparte. Der Blitz aus dem Logo. */
export const IconBolt = icon("IconBolt", <path d="M13 2.5 4.5 14H11l-1 7.5L19.5 10H13z" />);

/** Anlage, Standort. */
export const IconBuilding = icon(
  "IconBuilding",
  <>
    <path d="M4 21V6.4L12 3l8 3.4V21" />
    <path d="M2.5 21h19" />
    <path d="M9.5 21v-4.5h5V21" />
    <path d="M9 9.5h.01M15 9.5h.01M9 13h.01M15 13h.01" />
  </>,
);

/** Gerät — der Lesekopf, ein Chip mit Anschlüssen. */
export const IconDevice = icon(
  "IconDevice",
  <>
    <rect x="6.5" y="6.5" width="11" height="11" rx="2.5" />
    <path d="M10 3.5V6M14 3.5V6M10 18v2.5M14 18v2.5" />
    <path d="M3.5 10H6M3.5 14H6M18 10h2.5M18 14h2.5" />
  </>,
);

/** Zähler, Messstelle. */
export const IconGauge = icon(
  "IconGauge",
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 12 16.4 8.2" />
    <path d="M12 3v1.8M21 12h-1.8M3 12h1.8" />
  </>,
);

/** Integration — die Datenquelle, an die etwas angesteckt wird. */
export const IconPlug = icon(
  "IconPlug",
  <>
    <path d="M9 2.5V8M15 2.5V8" />
    <path d="M5.5 8h13v3.2a6.5 6.5 0 0 1-13 0z" />
    <path d="M12 17.7V21.5" />
  </>,
);

/** Einstellungen. */
export const IconGear = icon(
  "IconGear",
  <>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 2.5v2.6M12 18.9v2.6M2.5 12h2.6M18.9 12h2.6" />
    <path d="m5.3 5.3 1.9 1.9M16.8 16.8l1.9 1.9M18.7 5.3l-1.9 1.9M7.2 16.8l-1.9 1.9" />
  </>,
);

/** Das eigene Konto. */
export const IconUser = icon(
  "IconUser",
  <>
    <circle cx="12" cy="8" r="4" />
    <path d="M4.5 21c0-4 3.4-6 7.5-6s7.5 2 7.5 6" />
  </>,
);

/** Nutzerverwaltung. */
export const IconUsers = icon(
  "IconUsers",
  <>
    <circle cx="9.5" cy="8" r="3.8" />
    <path d="M2.5 21c0-3.8 3.1-5.8 7-5.8s7 2 7 5.8" />
    <path d="M16.5 4.6a3.8 3.8 0 0 1 0 6.8" />
    <path d="M18.5 15.7c1.9.7 3 2.2 3 5.3" />
  </>,
);

/** Zustand der Dienste — der Ausschlag eines laufenden Systems. */
export const IconActivity = icon("IconActivity", <path d="M2.5 12.5h4l3 7.5 4.5-16 3 8.5h4.5" />);

/** Vorlage, Schema. */
export const IconTemplate = icon(
  "IconTemplate",
  <>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M3 9.5h18" />
    <path d="M9.5 9.5V20" />
  </>,
);

/** Erzeugung. Eine Größe, keine Sparte — die Farbe kommt aus `QUANTITY_COLORS`. */
export const IconSun = icon(
  "IconSun",
  <>
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2.5v2.4M12 19.1v2.4M2.5 12h2.4M19.1 12h2.4" />
    <path d="m5.4 5.4 1.7 1.7M16.9 16.9l1.7 1.7M18.6 5.4l-1.7 1.7M7.1 16.9l-1.7 1.7" />
  </>,
);

/** Abgabe nach außen — die Einspeisung (HM-138), wie im Entwurf `IcArrowUpRight`. */
export const IconArrowUpRight = icon("IconArrowUpRight", <path d="M7 17 17 7M8 7h9v9" />);

/** Batterie — der Speicher (HM-138), als Gegenstand und nicht als Ladezustand. */
export const IconBattery = icon(
  "IconBattery",
  <>
    <rect x="2" y="7" width="16" height="10" rx="2" />
    <path d="M22 11v2" />
    <path d="M5 10v4h6v-4z" fill="currentColor" stroke="none" />
  </>,
);

/**
 * Pause — der angehaltene Ladepunkt (HM-119). Zwei Balken und kein Quadrat:
 * Ein Stopp hieße, dass danach nichts mehr kommt; das Fahrzeug bleibt angesteckt.
 */
export const IconPause = icon(
  "IconPause",
  <>
    <path d="M9 5v14" />
    <path d="M15 5v14" />
  </>,
);

/**
 * Das Blatt — „Sonne bevorzugt" am Ladepunkt (HM-119). Es steht für eine
 * **Betriebsart**, nicht für eine Größe; in einer Datendarstellung kommt es nicht vor.
 */
export const IconLeaf = icon(
  "IconLeaf",
  <>
    <path d="M5 19c0-8 5-13 14-14-1 9-6 14-14 14z" />
    <path d="M5 19 12 12" />
  </>,
);

/** Der Mond — am Themenumschalter (HM-107), das Gegenstück zur Sonne. */
export const IconMoon = icon(
  "IconMoon",
  <path d="M20 14.2A8.2 8.2 0 1 1 9.8 4a6.6 6.6 0 0 0 10.2 10.2z" />,
);

/**
 * Wohnung — eine Wohnungstür, nicht ein Gebäude.
 *
 * Ein zweites Haus neben `IconBuilding` wäre bei 15 px nicht zu unterscheiden,
 * und der Unterschied ist gerade der Punkt: Wer eine Wohnung führt, misst seine
 * Einheit und nicht das Haus darum herum.
 */
export const IconDoor = icon(
  "IconDoor",
  <>
    <path d="M5.5 21V4.6A1.6 1.6 0 0 1 7.1 3h9.8a1.6 1.6 0 0 1 1.6 1.6V21" />
    <path d="M3.5 21h17" />
    <circle cx="15" cy="12.6" r="1.1" fill="currentColor" stroke="none" />
  </>,
);

/** Gewerbe — die Ladenfront mit vorgezogenem Dach. */
export const IconStore = icon(
  "IconStore",
  <>
    <path d="M4.2 9.5 5.6 4.5h12.8l1.4 5" />
    <path d="M3.2 9.5h17.6" />
    <path d="M5.2 9.5V20.5h13.6V9.5" />
    <path d="M9.2 20.5V14h5.6v6.5" />
  </>,
);

/** Anlage — das Zackendach eines Werks samt Turm. */
export const IconFactory = icon(
  "IconFactory",
  <>
    <path d="M2.5 20.5V10l6 4.2V10l6 4.2V5.5h5.4v15" />
    <path d="M2 20.5h20" />
    <path d="M7 17.2h.01M12.5 17.2h.01M18 17.2h.01" />
  </>,
);

/**
 * Ein Ort ohne Aussage über seine Bauart — für „Sonstiges".
 *
 * Ein weiteres Gebäude an dieser Stelle behauptete eine Form, die gerade nicht
 * bekannt ist. Die Nadel sagt nur: hier steht etwas.
 */
export const IconPin = icon(
  "IconPin",
  <>
    <path d="M19.5 10.4c0 5.4-7.5 11.1-7.5 11.1s-7.5-5.7-7.5-11.1a7.5 7.5 0 1 1 15 0Z" />
    <circle cx="12" cy="10.2" r="2.7" />
  </>,
);

/** Weitere Möglichkeiten zu einer Zeile. */
export const IconMore = icon(
  "IconMore",
  <>
    <circle cx="12" cy="5.5" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="12" cy="18.5" r="1.4" fill="currentColor" stroke="none" />
  </>,
);

export const IconPlus = icon("IconPlus", <path d="M12 5v14M5 12h14" />);

export const IconCheck = icon("IconCheck", <path d="m4.5 12.5 5 5.5L19.5 6.5" />);

/** Schließen — das Kreuz eines Dialogs. Kein gedrehtes Plus: die Enden sind kürzer. */
export const IconClose = icon("IconClose", <path d="M6 6l12 12M18 6L6 18" />);

/**
 * Zurück — der Weg aus einer Detailansicht heraus.
 *
 * Ein **Pfeil**, kein Chevron: Der Chevron zeigt an, dass etwas aufgeht oder
 * tiefer führt; der Pfeil sagt „hier geht es zurück". Im Kit ist es ein um 180°
 * gedrehter Chevron — eine Drehung, die man an der Zeichnung nicht mehr abliest.
 */
export const IconArrowLeft = icon(
  "IconArrowLeft",
  <>
    <path d="M19 12H5" />
    <path d="m11 6-6 6 6 6" />
  </>,
);

/** Führt tiefer — das Zeichen am Ende einer Zeile, die sich öffnen lässt. */
export const IconChevronRight = icon("IconChevronRight", <path d="m9.5 5.5 6.5 6.5-6.5 6.5" />);

/** Der Kalender — am Datumsnavigator der Kopfleiste (HM-076). */
export const IconCalendar = icon(
  "IconCalendar",
  <>
    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
    <path d="M3.5 9.5h17M8 3v4M16 3v4" />
  </>,
);

/** Der Umschalter-Hinweis: hier lässt sich etwas auf- und zuklappen. */
export const IconUnfold = icon(
  "IconUnfold",
  <>
    <path d="m8 9.5 4-4 4 4" />
    <path d="m16 14.5-4 4-4-4" />
  </>,
);

/** Nachricht, Feedback (HM-169). */
export const IconMessage = icon("IconMessage", <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />);

/** Administration der Plattform — der Schild über den Admin-Bereichen. */
export const IconShield = icon(
  "IconShield",
  <path d="M12 2.8 4.5 5.6v5.9c0 4.6 3.1 8.4 7.5 9.7 4.4-1.3 7.5-5.1 7.5-9.7V5.6L12 2.8Z" />,
);

/** Fehler melden (HM-169). */
export const IconBug = icon(
  "IconBug",
  <>
    <rect x="8" y="6" width="8" height="14" rx="4" />
    <path d="M12 20v-9M8 13H4M20 13h-4M9 6.5 7.5 4M15 6.5 16.5 4M8 17l-3 2M16 17l3 2M8 9.5 5 8M16 9.5 19 8" />
  </>,
);

/** Wunsch äußern (HM-169). */
export const IconBulb = icon(
  "IconBulb",
  <>
    <path d="M9 18h6M10 21h4" />
    <path d="M12 3a6 6 0 0 0-3.6 10.8c.7.5 1.1 1.3 1.1 2.2h5c0-.9.4-1.7 1.1-2.2A6 6 0 0 0 12 3z" />
  </>,
);

/** Neuigkeiten (HM-170). */
export const IconSparkle = icon(
  "IconSparkle",
  <>
    <path d="M11 3.5 12.9 9l5.6 2-5.6 2L11 18.5 9.1 13l-5.6-2 5.6-2z" />
    <path d="M18.5 3v4M16.5 5h4" />
  </>,
);

/** Weiter zu etwas — ein Weg, kein Link nach außen (HM-170). */
export const IconArrowRight = icon("IconArrowRight", <path d="M5 12h14M13 6l6 6-6 6" />);
